/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, February 22nd 2022, 9:21:45 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import {
  useAPIRequest,
  useAPIFetchItem,
  useAPIMutation,
  APIMakeRequestFncArgs,
  UseLazyFetchListArgs,
  useAPILazyFetchList,
  useAPIFetchList,
  APISERVICEPATHS,
  useAPIForm,
} from "lib/api";
import {
  ProcessDefinition,
  ProcessDefinitionValidationSchema,
  ProcessDefinitionPatchOptions,
  ProcessInstanceDataTableResult,
  FormConfiguration,
  ProcessDefinitionSubmissionFormLinkage,
} from "lib/schemas";
import { PrimaryKeyValue } from "lib/types";

const apiServicePath = APISERVICEPATHS.processDefinitions;

const useAPIFetchProcessDefinition = () => {
  return useAPIFetchItem<ProcessDefinition>({
    apiServicePath,
    makeRequestOnInitialMount: false,
  });
};

const useAPIPatchProcessDefinition = (
  setValidAPIResponse: APIMakeRequestFncArgs<ProcessDefinition>["setValidAPIResponse"]
) => {
  const { patchData, ...otherValues } = useAPIMutation<ProcessDefinition>({ apiServicePath });

  const patchProcessDefinition = React.useCallback(
    (
      processDefinitionId: PrimaryKeyValue<ProcessDefinition>,
      processDefinitionData: ProcessDefinitionPatchOptions,
      _setValidAPIResponse: APIMakeRequestFncArgs<ProcessDefinition>["setValidAPIResponse"] = setValidAPIResponse
    ) => {
      processDefinitionId &&
        patchData({
          apiPathParams: [processDefinitionId],
          body: JSON.stringify(processDefinitionData),
          setValidAPIResponse: _setValidAPIResponse,
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [patchData]
  );

  return {
    patchProcessDefinition,
    ...otherValues,
  };
};

/***
 * useProcessDefinitionAPIForm: Hook for handling default values, post and retrieval logic for a process definition.
 * Note: This hook must be used in components wrapped within ServiceBuilderProvider.
 *
 */

const useProcessDefinitionAPIForm = (
  processDefinitionData: ProcessDefinition,
  handleAPISaveProcessDefinition: APIMakeRequestFncArgs<ProcessDefinition>["setValidAPIResponse"]
) => {
  const setDefaultSequence = (processDefinition: ProcessDefinition): ProcessDefinition => {
    if (!processDefinition.sequence) return { ...processDefinition, sequence: 0 };
    return processDefinition;
  };

  const defaultProcessDefinition = setDefaultSequence(processDefinitionData); // Just in case our sequence is null.

  const useAPIFormReturn = useAPIForm({
    apiInitialFetchId: null, // Since we have already fetched our process definition in the service builder context, there is no need to fetch from API. This ensures we adhere to the single source of truth paradigm and conform to the top-down approach / one-way data binding of react.
    apiFormDefaultValues: defaultProcessDefinition,
    apiFormDataValidationSchema: ProcessDefinitionValidationSchema,
    apiServicePath,
    setAPIFormResponse: handleAPISaveProcessDefinition,
    primaryKey: "id",
    titleKey: "title",
    method: "PATCH",
    formResetDependencyArray: [JSON.stringify(processDefinitionData)],
  });

  return {
    useAPIFormReturn,
  };
};

const useAPILazyLoadProcessDefinitions = (options: Omit<UseLazyFetchListArgs<ProcessDefinition>, "apiServicePath">) =>
  useAPILazyFetchList<ProcessDefinition>({
    apiServicePath,
    ...options,
  });

const useAPIProcessDefinitionSubmissionForms = () => {
  const { fetchList, ...otherProperties } = useAPIFetchList<FormConfiguration>({
    apiServicePath,
    makeRequestOnInitialMount: false,
  });

  const fetchSubmissionForms = React.useCallback(
    (processDefinitionId: ProcessDefinition["id"]) =>
      fetchList({ apiPathParams: [processDefinitionId, "submission_forms"] }),
    [fetchList]
  );

  return { fetchSubmissionForms, ...otherProperties };
};

const useAPIProcessDefinitionServiceForms = () => {
  const { fetchList, ...otherProperties } = useAPIFetchList<FormConfiguration>({
    apiServicePath,
    makeRequestOnInitialMount: false,
  });

  const fetchServiceForms = React.useCallback(
    (processDefinitionId: ProcessDefinition["id"]) =>
      fetchList({ apiPathParams: [processDefinitionId, "service_registration_forms"] }),
    [fetchList]
  );

  return { fetchServiceForms, ...otherProperties };
};

const useAPIProcessInstanceDTQuery = () => {
  const { makeRequest, makeRequestCleanUp, ...otherProperties } = useAPIRequest<ProcessInstanceDataTableResult>({
    apiServicePath,
    makeRequestOnInitialMount: false,
  });

  const fetchDTData = React.useCallback(
    (
      processDefinitionId: ProcessDefinition["id"],
      onFetchDTData: APIMakeRequestFncArgs<ProcessInstanceDataTableResult>["setValidAPIResponse"]
    ) =>
      makeRequest({
        setValidAPIResponse: onFetchDTData,
        apiPathParams: [processDefinitionId, "run-instance-list-query"],
      }),
    [makeRequest]
  );

  return { fetchDTData, ...otherProperties };
};

const useProcessFormLinkageAPI = () => {
  const { makeRequest, makeRequestCleanUp, ...otherProperties } = useAPIRequest<
    ProcessDefinitionSubmissionFormLinkage | ProcessDefinitionSubmissionFormLinkage[]
  >({
    apiServicePath: APISERVICEPATHS.processDefinitionSubmissionFormLinkages,
    makeRequestOnInitialMount: false,
  });

  const fetchLinkages = React.useCallback(
    (
      processDefinitionId: ProcessDefinition["id"],
      onFetchData: APIMakeRequestFncArgs<ProcessDefinitionSubmissionFormLinkage[]>["setValidAPIResponse"]
    ) =>
      makeRequest({
        setValidAPIResponse: onFetchData,
        apiQueryParams: { process_definition: processDefinitionId },
      }),
    [makeRequest]
  );

  const patchLinkage = React.useCallback(
    (
      processDefinitionId: ProcessDefinition["id"],
      linkageId: ProcessDefinitionSubmissionFormLinkage["id"],
      data: Partial<ProcessDefinitionSubmissionFormLinkage>,
      onPatch: APIMakeRequestFncArgs<ProcessDefinitionSubmissionFormLinkage>["setValidAPIResponse"]
    ) =>
      makeRequest({
        setValidAPIResponse: onPatch,
        method: "PATCH",
        apiPathParams: [linkageId],
        apiQueryParams: { process_definition: processDefinitionId },
        body: JSON.stringify(data),
      }),
    [makeRequest]
  );

  return { fetchLinkages, patchLinkage, ...otherProperties };
};

export const useProcessDefinitionsAPI = () => ({
  useAPIFetchProcessDefinition,
  useAPIPatchProcessDefinition,
  useProcessDefinitionAPIForm,
  useAPILazyLoadProcessDefinitions,
  useAPIProcessDefinitionSubmissionForms,
  useAPIProcessDefinitionServiceForms,
  useAPIProcessInstanceDTQuery,
  useProcessFormLinkageAPI,
});
